import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface GlobalStats {
  totalVolume24h: string;
  totalVolumeChange24h: number;
  totalSales24h: number;
  totalSalesChange24h: number;
  totalCollections: number;
  activeCollections24h: number;
  totalNFTs: number;
  totalOwners: number;
  averageSalePrice: string;
  updatedAt: string;
}

export const AnalyticsPage: React.FC = () => {
  const [globalStats, setGlobalStats] = useState<GlobalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadGlobalStats();
  }, []);

  const loadGlobalStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<any>('/analytics/global');
      if (response.success && response.data) {
        setGlobalStats(response.data);
      }
    } catch (err) {
      console.error('Error loading global stats:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatVolume = (volume: string): string => {
    const num = parseFloat(volume);
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M XRP`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K XRP`;
    }
    return `${num.toLocaleString()} XRP`;
  };

  return (
    <div className="analytics-page" style={{ minHeight: '100vh', background: '#0a0e1a', color: '#fff' }}>
      <style>{`
        @media (max-width: 767px) {
          .analytics-page header { padding: 1rem !important; }
          .analytics-page h1 { font-size: 1.5rem !important; }
          .analytics-page .stats-section { padding: 1rem !important; }
          .analytics-page .stats-grid { gap: 1rem !important; grid-template-columns: 1fr !important; }
          .analytics-page .stat-card { padding: 1rem !important; }
          .analytics-page .stat-value { font-size: 1.5rem !important; }
        }
      `}</style>
      {/* Header */}
      <header style={{ padding: '2rem', background: '#1a1f3a' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>📈 Global Analytics</h1>
        </div>
      </header>

      {/* Global Stats */}
      <section className="stats-section" style={{ padding: '2rem' }}>
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem 0' }}>
            <LoadingSpinner size="md" />
            <p style={{ marginTop: '1rem', color: 'rgba(255, 255, 255, 0.6)' }}>Loading analytics...</p>
          </div>
        ) : error ? (
          <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center', padding: '4rem 0' }}>
            <p style={{ color: '#ef4444', marginBottom: '1rem' }}>{error}</p>
            <button
              onClick={loadGlobalStats}
              style={{ padding: '0.75rem 1.5rem', background: '#2563eb', color: 'white', border: 'none', borderRadius: '0.5rem', fontWeight: 500, cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        ) : globalStats ? (
          <div
            className="stats-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '1.5rem',
              maxWidth: '1200px',
              margin: '0 auto',
            }}
          >
            {/* 24h Volume */}
            <div
              className="stat-card"
              style={{
                background: 'rgba(26, 31, 58, 0.5)',
                padding: '1.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', margin: 0 }}>
                24h Volume
              </h3>
              <p className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', margin: '0.5rem 0' }}>
                {formatVolume(globalStats.totalVolume24h)}
              </p>
              <span
                style={{
                  fontSize: '0.875rem',
                  color: globalStats.totalVolumeChange24h >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {globalStats.totalVolumeChange24h >= 0 ? '+' : ''}
                {globalStats.totalVolumeChange24h.toFixed(2)}%
              </span>
            </div>

            {/* 24h Sales */}
            <div
              className="stat-card"
              style={{
                background: 'rgba(26, 31, 58, 0.5)',
                padding: '1.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', margin: 0 }}>
                24h Sales
              </h3>
              <p className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, marginBottom: '0.5rem', margin: '0.5rem 0' }}>
                {formatNumber(globalStats.totalSales24h)}
              </p>
              <span
                style={{
                  fontSize: '0.875rem',
                  color: globalStats.totalSalesChange24h >= 0 ? '#10b981' : '#ef4444',
                }}
              >
                {globalStats.totalSalesChange24h >= 0 ? '+' : ''}
                {globalStats.totalSalesChange24h.toFixed(2)}%
              </span>
            </div>

            {/* Total Collections */}
            <div
              className="stat-card"
              style={{
                background: 'rgba(26, 31, 58, 0.5)',
                padding: '1.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', margin: 0 }}>
                Total Collections
              </h3>
              <p className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, margin: '0.5rem 0' }}>
                {formatNumber(globalStats.totalCollections)}
              </p>
              <span style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                {formatNumber(globalStats.activeCollections24h)} active 24h
              </span>
            </div>

            {/* Total NFTs */}
            <div
              className="stat-card"
              style={{
                background: 'rgba(26, 31, 58, 0.5)',
                padding: '1.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', margin: 0 }}>
                Total NFTs
              </h3>
              <p className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, margin: '0.5rem 0' }}>
                {formatNumber(globalStats.totalNFTs)}
              </p>
              <span style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                {formatNumber(globalStats.totalOwners)} owners
              </span>
            </div>

            {/* Average Sale Price */}
            <div
              className="stat-card"
              style={{
                background: 'rgba(26, 31, 58, 0.5)',
                padding: '1.5rem',
                borderRadius: '1rem',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <h3 style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '0.5rem', margin: 0 }}>
                Average Sale Price
              </h3>
              <p className="stat-value" style={{ fontSize: '2rem', fontWeight: 700, margin: '0.5rem 0' }}>
                {formatVolume(globalStats.averageSalePrice)}
              </p>
              <span style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                Last 24h
              </span>
            </div>
          </div>
        ) : null}
      </section>
    </div>
  );
};
